import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import Svg, { Rect, Defs, LinearGradient, Stop } from 'react-native-svg';

interface HomeIconProps {
  focused: boolean;
  color?: string;
  size?: number;
}

const AnimatedRect = Animated.createAnimatedComponent(Rect);

const HomeIcon: React.FC<HomeIconProps> = ({ focused, color = '#1C274C', size = 24 }) => {
  const fillOpacity = useRef(new Animated.Value(focused ? 1 : 0)).current;
  const strokeOpacity = useRef(new Animated.Value(focused ? 0 : 1)).current;
  const scale = useRef(new Animated.Value(focused ? 1.05 : 0.95)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(fillOpacity, {
        toValue: focused ? 1 : 0,
        useNativeDriver: false,
        friction: 8,
      }),
      Animated.spring(strokeOpacity, {
        toValue: focused ? 0 : 1,
        useNativeDriver: false,
        friction: 8,
      }),
      Animated.spring(scale, {
        toValue: focused ? 1.05 : 0.95,
        useNativeDriver: true,
        friction: 8,
      }),
    ]).start();
  }, [focused]);

  // Scale factor to fit viewBox 0 0 105 104 into size (default 24)
  const scaleFactor = size / 105;

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Svg width={size} height={size * (104/105)} viewBox="0 0 105 104" fill="none">
        <Defs>
          <LinearGradient id="homeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <Stop offset="0%" stopColor="#D4A655" stopOpacity="1" />
            <Stop offset="50%" stopColor="#C4963E" stopOpacity="1" />
            <Stop offset="100%" stopColor="#A67D2E" stopOpacity="1" />
          </LinearGradient>
        </Defs>
        
        {/* Filled version (focused) */}
        <AnimatedRect
          x="4"
          y="4"
          width="38"
          height="48"
          rx="6"
          fill={focused ? "url(#homeGradient)" : "none"}
          stroke={focused ? "url(#homeGradient)" : "none"}
          strokeWidth="8"
          opacity={fillOpacity}
        />
        <AnimatedRect
          x="101"
          y="100"
          width="38"
          height="48"
          rx="6"
          transform="rotate(-180 101 100)"
          fill={focused ? "url(#homeGradient)" : "none"}
          stroke={focused ? "url(#homeGradient)" : "none"}
          strokeWidth="8"
          opacity={fillOpacity}
        />
        <AnimatedRect
          x="4"
          y="73"
          width="38"
          height="27"
          rx="6"
          fill={focused ? "url(#homeGradient)" : "none"}
          stroke={focused ? "url(#homeGradient)" : "none"}
          strokeWidth="8"
          opacity={fillOpacity}
        />
        <AnimatedRect
          x="101"
          y="31"
          width="38"
          height="27"
          rx="6"
          transform="rotate(-180 101 31)"
          fill={focused ? "url(#homeGradient)" : "none"}
          stroke={focused ? "url(#homeGradient)" : "none"}
          strokeWidth="8"
          opacity={fillOpacity}
        />
        
        {/* Outline version (not focused) */}
        <AnimatedRect
          x="4"
          y="4"
          width="38"
          height="48"
          rx="6"
          stroke={color}
          strokeWidth="8"
          fill="none"
          opacity={strokeOpacity}
        />
        <AnimatedRect
          x="101"
          y="100"
          width="38"
          height="48"
          rx="6"
          transform="rotate(-180 101 100)"
          stroke={color}
          strokeWidth="8"
          fill="none"
          opacity={strokeOpacity}
        />
        <AnimatedRect
          x="4"
          y="73"
          width="38"
          height="27"
          rx="6"
          stroke={color}
          strokeWidth="8"
          fill="none"
          opacity={strokeOpacity}
        />
        <AnimatedRect
          x="101"
          y="31"
          width="38"
          height="27"
          rx="6"
          transform="rotate(-180 101 31)"
          stroke={color}
          strokeWidth="8"
          fill="none"
          opacity={strokeOpacity}
        />
      </Svg>
    </Animated.View>
  );
};

export default HomeIcon;
